import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Send, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { chatAPI } from '../services/api';

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [userName, setUserName] = useState('');
  const [isNameSet, setIsNameSet] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Initialize session
  useEffect(() => {
    let storedSessionId = localStorage.getItem('lihkab_chat_session');
    if (!storedSessionId) {
      storedSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('lihkab_chat_session', storedSessionId);
    }
    setSessionId(storedSessionId);

    // Check if name is already set
    const storedName = localStorage.getItem('lihkab_chat_name');
    if (storedName) {
      setUserName(storedName);
      setIsNameSet(true);
      loadMessages(storedSessionId);
    }
  }, []);

  // Auto scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async (sid) => {
    try {
      const response = await chatAPI.getMessages(sid);
      if (response.success) {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  const handleNameSubmit = (e) => {
    e.preventDefault();
    if (userName.trim()) {
      localStorage.setItem('lihkab_chat_name', userName.trim());
      setIsNameSet(true);
      loadMessages(sessionId);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isLoading) return;

    setIsLoading(true);
    try {
      const response = await chatAPI.sendMessage({
        session_id: sessionId,
        name: userName,
        message: newMessage.trim()
      });

      if (response.success) {
        setMessages([...messages, response.data]);
        setNewMessage('');
        toast.success('Mesaj gönderildi');
      }
    } catch (error) {
      toast.error('Mesaj gönderilemedi');
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 bg-sky-600 hover:bg-sky-700 text-white rounded-full p-4 shadow-2xl hover:shadow-xl transition-all duration-300 z-50 group animate-bounce hover:animate-none"
          aria-label="Chat'i aç"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold animate-pulse">
            !
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl z-50 border border-gray-200 transition-all duration-300 ${
            isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
          } flex flex-col overflow-hidden`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-sky-600 to-sky-700 text-white p-4 flex items-center justify-between rounded-t-2xl">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-sky-600" />
              </div>
              <div>
                <h3 className="font-bold text-lg">Canlı Destek</h3>
                <p className="text-xs text-sky-100">Genellikle birkaç dakika içinde yanıt verilir</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className="hover:bg-sky-500 rounded-full p-2 transition-colors"
                aria-label="Küçült"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="hover:bg-sky-500 rounded-full p-2 transition-colors"
                aria-label="Kapat"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content */}
          {!isMinimized && (
            <>
              {!isNameSet ? (
                // Name Input
                <div className="flex-1 flex items-center justify-center p-6">
                  <form onSubmit={handleNameSubmit} className="w-full space-y-4">
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Hoş Geldiniz!</h4>
                      <p className="text-gray-600 text-sm">Size nasıl yardımcı olabiliriz?</p>
                    </div>
                    <Input
                      type="text"
                      placeholder="Adınızı girin"
                      value={userName}
                      onChange={(e) => setUserName(e.target.value)}
                      className="w-full"
                      required
                    />
                    <Button
                      type="submit"
                      className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                    >
                      Sohbete Başla
                    </Button>
                  </form>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 mt-8">
                        <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm">Henüz mesaj yok.</p>
                        <p className="text-xs mt-1">Size nasıl yardımcı olabiliriz?</p>
                      </div>
                    ) : (
                      messages.map((msg, index) => (
                        <div
                          key={index}
                          className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'}`}
                        >
                          <div
                            className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                              msg.is_admin
                                ? 'bg-white border border-gray-200 text-gray-900'
                                : 'bg-sky-600 text-white'
                            } shadow-sm`}
                          >
                            {msg.is_admin && (
                              <p className="text-xs font-semibold mb-1 text-sky-600">Destek Ekibi</p>
                            )}
                            <p className="text-sm break-words">{msg.message}</p>
                            <p className={`text-xs mt-1 ${
                              msg.is_admin ? 'text-gray-400' : 'text-sky-100'
                            }`}>
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="p-4 bg-white border-t border-gray-200">
                    <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                      <Input
                        type="text"
                        placeholder="Mesajınızı yazın..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={isLoading}
                        className="flex-1"
                      />
                      <Button
                        type="submit"
                        disabled={isLoading || !newMessage.trim()}
                        className="bg-sky-600 hover:bg-sky-700 text-white px-4"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
};

export default FloatingChat;